import BlockContainer from '../../core/components/layout/BlockContainer'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemTextProps,
    Stack,
    Typography,
    TypographyProps,
    useTheme,
} from '@mui/material';
import { CreditCard, InfoOutlined } from '@mui/icons-material';
import { useSelector } from 'react-redux'

import moment from 'moment'
import { RootState } from '../../core/store'
import InfoAlert from '../../core/components/ui/InfoAlert'
import { useCancelSubscriptionMutation, useGetStudentSubscriptionQuery } from './api/billing.api'
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../../core/components/ui/ConfitmDialog';
import ConfirmationDialog from '../../core/components/ui/ConfirmationDialog';
import { useEffect, useState } from 'react';
import toastService from '../../services/toastService';
import ApiErrorLabel from '../../core/components/ui/ApiErrorLabel';
import { TypeChip } from './PaymentHistory';
interface CustomListItemTextProps extends ListItemTextProps {
    isLast?: boolean;
    secondaryColor?: string;
    primaryColor?: string;
    secondaryStyle?: TypographyProps
}
const CustomListItemText = (props: CustomListItemTextProps) => {
    const theme = useTheme();
    return <ListItem component="div" sx={{
        borderBottom: props.isLast ? 'none' : `1px solid ${theme.palette.grey[200]}`,
        pl: 0,
        pr: 0,
        '& .MuiListItemSecondaryAction-root': {
            right: 0
        }
    }} secondaryAction={<Typography variant='button' sx={{ fontWeight: 500, fontSize: theme.typography.fontSizes.md, color: props.secondaryColor, }} {...props.secondaryStyle}>{props.secondary || 'N/A'}</Typography>}>
        <ListItemText primary={<Typography variant='button' sx={{ fontWeight: 400, color: props.primaryColor ?? theme.palette.grey[500] }}>{props.primary}</Typography>} />
    </ListItem>
}

const TRIAL_MOMENTS = 15;

function transformLabel(label: string, isTrial: boolean) {
    if (isTrial) return "One-time";
    switch (label) {
        case "month":
            return "Monthly"
        default:
            return "N/A"
    }
}

function isTrialSubscription(subscription: { isInTrial?: boolean; trialStatus?: string } | undefined): boolean {
    return (
        subscription?.isInTrial === true ||
        subscription?.trialStatus?.toLowerCase() === 'active'
    );
}

function isSubscriptionCanceledStatus(subscription: { status?: string } | undefined): boolean {
    return subscription?.status?.toUpperCase() === 'CANCELED' || subscription?.status?.toUpperCase() === 'CANCELLED';
}

function ManageSubscription() {
    const theme = useTheme();
    const { data, isLoading, refetch } = useGetStudentSubscriptionQuery(undefined, {
        refetchOnMountOrArgChange: true,
    });
    const [cancelSubscription, { error, isError }] = useCancelSubscriptionMutation()
    const subscription = data?.data.data;
    const isSubscriptionCanceled = subscription?.cancelPlanInitiated;
    const isCanceled = isSubscriptionCanceled || isSubscriptionCanceledStatus(subscription);
    const navigate = useNavigate();
    const [isConfirmation, setConfirmation] = useState(false)
    const [isApiError, setApiError] = useState(false);
    const [isCanceling, setCanceling] = useState(false);
    const [isChangePlanConfirm, setChangePlanConfirm] = useState(false);

    const handleCancelSubscription = async () => {
        setCanceling(true);
        try {
            const response = await cancelSubscription().unwrap();
            if (response?.data?.statusCode === 200) {
                toastService.success(response?.data?.message);
                await refetch();
            }
        } catch (error) {
            setApiError(true);
        } finally {
            setCanceling(false);
            setConfirmation(false)
        }
    }
    useEffect(() => {
        ; (async () => {
            await refetch();
        })()
    }, [])
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4
        }}>
            <BlockContainer sx={{ paddingLeft: '10px', paddingRight: '10px' }}>
                <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} gap={3} mb={3}>
                    <CreditCard sx={{ color: theme.palette.primary[400] }} />
                    <Typography variant='h6' sx={{ color: theme.palette.grey[800], fontWeight: 600 }}>Current Subscription</Typography>
                </Stack>
                <Stack>
                    {isLoading ?
                        <Box sx={{ height: 268, }} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                            <CircularProgress size="30px" />
                        </Box>
                        : <List sx={{ width: '100%', }}  >
                            <CustomListItemText
                                primary="Subscription Plan"
                                secondary={isTrialSubscription(subscription) ? '7-Day Free Trial' : subscription?.title}
                                secondaryStyle={{ textTransform: 'capitalize' }}
                            />
                            <CustomListItemText
                                primary="Billing Cycle"
                                secondary={transformLabel(subscription?.billingCycle!, isTrialSubscription(subscription))}
                            />
                            <CustomListItemText primary="Status" secondary={
                                <TypeChip label={subscription?.status!} />
                            } />

                            {subscription ? (isSubscriptionCanceledStatus(subscription)
                                ? <CustomListItemText primary="Plan canceled" secondary="—" />
                                : isSubscriptionCanceled
                                    ? <CustomListItemText primary="Plan Cancel" secondary={moment(subscription?.nextBillingDate).format('MMM DD, YYYY')} />
                                    : <CustomListItemText primary="Next Billing Date" secondary={moment(subscription?.nextBillingDate).format('MMM DD, YYYY')} />) :
                                <CustomListItemText primary="Next Billing Date" />
                            }
                            {isSubscriptionCanceledStatus(subscription) ? <CustomListItemText primary="Monthly Cost" secondary="N/A" /> : (subscription?.cost ? <CustomListItemText primary="Monthly Cost" secondary={`$${subscription?.cost?.toFixed(2)}`} /> : <CustomListItemText primary="Monthly Cost" secondary={'N/A'} />)}
                            <CustomListItemText
                                primary="Moments Included"
                                secondary={
                                    isSubscriptionCanceledStatus(subscription)
                                        ? 'N/A'
                                        : isTrialSubscription(subscription)
                                            ? `${TRIAL_MOMENTS} MindRight Moments`
                                            : `${subscription?.moments || 'N/A'} MindRight Moments / month`
                                }
                                secondaryColor={isSubscriptionCanceledStatus(subscription) ? undefined : theme.palette.success.main}
                            />
                            {/* <CustomListItemText primary="Special Bonuses" secondary={`${memberSubscription.messagesLimit} bonus Moments (Early Access)`} isLast={true} /> */}
                        </List>}
                    {isCanceled ? <InfoAlert
                        backgroundColor={theme.palette.grey[200]}
                        customIcon={<InfoOutlined sx={{ color: theme.palette.grey[600] }} />}
                        sx={{ alignItems: 'center', mb: 0, mt: 2 }}>
                        <Typography variant='body1' sx={{ fontWeight: 400, color: theme.palette.grey[600], ml: 3, fontSize: theme.typography.fontSizes.sm, }}>
                            Your current plan has been canceled. To continue enjoying our services without interruption, please choose a new plan now.
                        </Typography>
                    </InfoAlert> : <InfoAlert
                        backgroundColor={theme.palette.secondary.light}
                        customIcon={<InfoOutlined sx={{ color: theme.palette.secondary[600] }} />}
                        sx={{ alignItems: 'center', mb: 0, mt: 2 }}>
                        <Typography variant='body1' sx={{ fontWeight: 400, color: theme.palette.secondary[600], ml: 3, fontSize: theme.typography.fontSizes.sm, }}>
                            Your subscription renews automatically. You can cancel at any time before your next billing date.
                        </Typography>
                    </InfoAlert>
                    }

                    <Stack direction={'row'} justifyContent={'flex-end'} alignItems={'center'} gap={3} mb={3} mt={3}>

                        {!isCanceled && <Button variant='outlined' color='info'
                            onClick={() => setChangePlanConfirm(true)}
                            sx={{
                                padding: '8px 20px',
                                borderColor: theme.palette.primary[400],
                                borderRadius: '6px',
                                color: theme.palette.primary[400],
                                '&:hover': {
                                    backgroundColor: theme.palette.primary[400],
                                    color: theme.palette.common.white,
                                }
                            }} >Change Plan</Button>}
                        {subscription && (isCanceled ? <Button variant='contained' color='primary'
                            sx={{
                                backgroundColor: theme.palette.primary[400],
                                padding: '8px 20px',
                                borderRadius: '6px',
                                '&:hover': {
                                    backgroundColor: theme.palette.primary[500],
                                }
                            }}
                            onClick={() => navigate('/subscriptions')}
                        >Buy Now</Button> : <Button variant='contained' color='error'
                            sx={{
                                backgroundColor: 'red',
                                padding: '8px 20px',
                                borderRadius: '6px',
                                '&:hover': {
                                    backgroundColor: 'red',
                                }
                            }}
                            disabled={isCanceled}
                            onClick={() => setConfirmation(true)}
                        >Cancel Subscription</Button>)}

                    </Stack>
                </Stack>
                <ApiErrorLabel
                    dismissible
                    message={error?.data?.message}
                    show={isApiError}
                    onDismiss={() => setApiError(false)}
                />
            </BlockContainer>
            {/* <div id='auto-recharge'>
                <BlockContainer sx={{ paddingLeft: '10px', paddingRight: '10px' }}>
                    <AutoRecharge />
                </BlockContainer>
            </div> */}
            <ConfirmationDialog
                open={isConfirmation}
                onCancel={() => setConfirmation(false)}
                message='Are you sure you want to cancel your subscription? You will forfeit any remaining balance.'
                onConfirm={handleCancelSubscription}
                title='Cancel Membership'
                confirmLabel='Yes, Cancel'
                cancelLabel="No"
                confirmColor='error'
                isLoading={isCanceling}
            />
            <ConfirmationDialog
                open={isChangePlanConfirm}
                onCancel={() => setChangePlanConfirm(false)}
                message={"Are you sure you want to change your membership? You will forfeit any remaining balance."}
                onConfirm={() => navigate('/subscriptions')}
                title='Change Membership'
                confirmLabel='Yes, Change'
                cancelLabel="No"
                confirmColor='primary'
                confirmSx={{
                    style: {
                        background: theme.palette.primary[400]
                    }
                }}
            />
        </Box>
    )
}

export default ManageSubscription